import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiUrl } from '../../apiConfig';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [message, setMessage] = useState({ text: '', type: '', visible: false });
  const [focusedField, setFocusedField] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Detect autofill and update focused fields
  useEffect(() => {
    const checkAutofill = () => {
      const usernameInput = document.querySelector('input[name="username"]');
      const passwordInput = document.querySelector('input[name="password"]');
      
      if (usernameInput && usernameInput.value) {
        setFormData(prev => ({ ...prev, username: usernameInput.value }));
      }
      if (passwordInput && passwordInput.value) {
        setFormData(prev => ({ ...prev, password: passwordInput.value }));
      }
    };

    // Check after a short delay to allow browser autofill to complete
    const timer = setTimeout(checkAutofill, 100);
    
    return () => clearTimeout(timer);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFocus = (fieldName) => {
    setFocusedField(fieldName);
  };

  const handleBlur = (fieldName) => {
    if (!formData[fieldName]) {
      setFocusedField('');
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type, visible: true });
    setTimeout(() => {
      setMessage(prev => ({ ...prev, visible: false }));
    }, 3000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      showMessage('Please fill in all fields', 'error');
      return;
    }
    
    setIsLoading(true);
    showMessage('Signing in...', 'info');
    
    try {
      // First get CSRF token
      const csrfResponse = await fetch(apiUrl('/api/get-csrf-token/'), {
        method: 'GET',
        credentials: 'include',
      });
      
      if (!csrfResponse.ok) {
        throw new Error('Failed to get CSRF token');
      }
      
      const csrfData = await csrfResponse.json();
      const csrfToken = csrfData.csrfToken;
      
      const response = await fetch(apiUrl('/api/login/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
        }),
        credentials: 'include', // This is important for handling cookies
      });

      const data = await response.json();

      if (response.ok) {
        showMessage('Welcome back! Redirecting...', 'success');
        // Use AuthContext to login
        login(data.user);
        // Redirect to upload page after successful login
        setTimeout(() => {
          navigate('/upload');
        }, 1500);
      } else {
        showMessage(data.detail || 'Login failed. Please try again.', 'error');
      }
    } catch (error) {
      showMessage('An error occurred. Please try again later.', 'error');
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-[#DFDAD3] min-h-screen flex items-center justify-center p-8">
      {/* Success/Error Message */}
      {message.visible && (
        <div className={`fixed top-8 right-8 px-6 py-4 rounded-xl shadow-lg ${
          message.type === 'error' ? 'bg-red-500 text-white' :
          message.type === 'success' ? 'bg-green-500 text-white' :
          'bg-[#2C2C2C] text-white'
        } text-sm font-medium z-50 animate-fade-in`}>
          {message.text}
        </div>
      )}

      {/* Centered Login Card */}
      <div className="w-full max-w-md">
        {/* Card Container */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-br from-[#2C2C2C] to-[#1a1a1a] px-8 py-12 text-center">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Admin Portal</h1>
            <p className="text-white/70 text-sm">Sign in to access your dashboard</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="px-8 py-10 space-y-6">
            {/* Username Field with Floating Label */}
            <div className="relative">
              <input 
                type="text" 
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                onFocus={() => handleFocus('username')}
                onBlur={() => handleBlur('username')}
                required
                className="peer w-full bg-[#F8F7F5] border-2 border-[#E8E6E1] rounded-xl py-4 px-4 text-base text-[#2C2C2C] focus:outline-none focus:border-[#2C2C2C] focus:bg-white transition-all duration-200 autofill:bg-white"
                placeholder=" "
                autoComplete="username"
              />
              <label className={`absolute left-4 transition-all duration-200 pointer-events-none peer-autofill:-top-2.5 peer-autofill:text-xs peer-autofill:bg-white peer-autofill:px-2 peer-autofill:text-[#2C2C2C] peer-autofill:font-medium ${
                focusedField === 'username' || formData.username
                  ? '-top-2.5 text-xs bg-white px-2 text-[#2C2C2C] font-medium'
                  : 'top-4 text-base text-[#999]'
              }`}>
                Username
              </label>
            </div>

            {/* Password Field with Floating Label */}
            <div className="relative">
              <input 
                type="password" 
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                onFocus={() => handleFocus('password')}
                onBlur={() => handleBlur('password')}
                required
                className="peer w-full bg-[#F8F7F5] border-2 border-[#E8E6E1] rounded-xl py-4 px-4 text-base text-[#2C2C2C] focus:outline-none focus:border-[#2C2C2C] focus:bg-white transition-all duration-200 autofill:bg-white"
                placeholder=" "
                autoComplete="current-password"
              />
              <label className={`absolute left-4 transition-all duration-200 pointer-events-none peer-autofill:-top-2.5 peer-autofill:text-xs peer-autofill:bg-white peer-autofill:px-2 peer-autofill:text-[#2C2C2C] peer-autofill:font-medium ${
                focusedField === 'password' || formData.password
                  ? '-top-2.5 text-xs bg-white px-2 text-[#2C2C2C] font-medium'
                  : 'top-4 text-base text-[#999]'
              }`}>
                Password
              </label>
            </div>

            {/* Login Button */}
            <button 
              type="submit"
              disabled={isLoading}
              className={`w-full bg-gradient-to-r from-[#2C2C2C] to-[#1a1a1a] text-white py-4 px-8 rounded-xl text-sm font-semibold uppercase tracking-wide shadow-lg transition-all duration-200 ${
                isLoading 
                  ? 'opacity-70 cursor-not-allowed' 
                  : 'hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]'
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing In...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="px-8 py-6 bg-[#F8F7F5] border-t border-[#E8E6E1]">
            <p className="text-center text-xs text-[#999]">&copy; 2024 Admin Portal. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;